import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "../zodios/client";
import { successToast, errorToast } from "../utils/toasts";
import { useAppSelector } from "../redux/hooks";
import { scopedBatch } from "../utils/formily";
import type { Form } from "@formily/core";

const TARGET_TYPE_TO_LABEL = {
  target: "Target",
  keyword: "Keyword",
} as const;

export function useUpdateTargetBids(targetType: string) {
  const { t } = useTranslation(["advertising"]);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const queryClient = useQueryClient();

  const targetLabelKey =
    targetType in TARGET_TYPE_TO_LABEL ?
      TARGET_TYPE_TO_LABEL[targetType as keyof typeof TARGET_TYPE_TO_LABEL]
    : "Target";

  const mutation = useMutation({
    mutationFn: async (data: {
      bids: Record<string, number>;
      action: "set_bid";
      target_type: string;
      channel: string;
    }) => {
      return await apiClient.targetAction({ data });
    },
    onSuccess: (_data, variables) => {
      successToast({
        message: t(($) => $.advertising["{targetType} Bid Settings Successfully Updated"], {
          targetType: t(($) => $.advertising.target_type[targetLabelKey]),
        }),
      });
      const idField = targetType === "target" ? "adv_target_id" : "adv_keyword_id";
      queryClient.setQueriesData<{ data: Array<Record<string, unknown>> }>(
        {
          predicate: (query) => {
            const key = query.queryKey[0];
            return (
              key != null && typeof key === "object" && (key as { entity?: string }).entity === "V4TargetReportByTarget"
            );
          },
        },
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((row) => {
              const bid = variables.bids[String(row[idField])];
              return bid === undefined ? row : { ...row, bid_resolved: bid };
            }),
          };
        },
      );
      // Mark stale but don't refetch now — avoids snap-back from pipeline lag
      void queryClient.invalidateQueries({ refetchType: "none" });
    },
    onError: (error) => {
      errorToast({
        message: t(($) => $.advertising["Failed to Update {targetType} Bid Settings"], {
          targetType: t(($) => $.advertising.target_type[targetLabelKey]),
        }),
        error,
      });
    },
  });

  const submitBids = async (values: Record<string, { bid_resolved?: number }>, form: Form) => {
    const bids: Record<string, number> = {};

    for (const [id, row] of Object.entries(values)) {
      if (row.bid_resolved != null && !Number.isNaN(row.bid_resolved)) {
        bids[id] = row.bid_resolved;
      }
    }

    if (Object.keys(bids).length === 0) return;

    try {
      await mutation.mutateAsync({
        bids,
        action: "set_bid",
        target_type: targetType,
        channel,
      });
    } catch {
      return;
    }

    scopedBatch(() => {
      form.setInitialValues(form.values);
      void form.reset();
    });
  };

  return { submitBids };
}
